/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useImperativeHandle, } from 'react';
import clsx from 'clsx';
import { styled, Popper as MUIPopper, Box, Fade, useForkRef, Backdrop, backdropClasses, } from '@mui/material';
import { IconClose } from '../../internal/icons';
import { Button } from '../../Button';
import { FooterButton } from './FooterButton';
import { popoverClasses, getStyle, getBackdropStyle } from './style';
import { useTranslation } from '../../hooks/useTranslation';
import { useUniqueId } from '../../hooks/useUniqueId';
import { useMixedValue } from '../../hooks/useMixedValue';
import { FocusLimitContainer } from '../../Shared/FocusLimitContainer';
import { extractAriaProps } from '../../Shared/props';
const PopoverBase = (props) => {
    const { t } = useTranslation('Shared');
    // In the following case, focus into the trigger should not redirect the focus onto the container,
    // since the user can access the content by tabbing again into it.
    const isTransitional = 
    // eslint-disable-next-line react/destructuring-assignment
    props.disablePortal &&
        // eslint-disable-next-line react/destructuring-assignment
        (props.trigger === 'hover' || props.trigger === 'focus');
    const { open, defaultOpen, className, closable, width, maxWidth, title, content, footer, okText = t('ok'), cancelText = t('cancel'), cancelButtonProps, okButtonProps, onOpen, onOk, onCancel, onClose, children, loopFocus, autoFocus = !isTransitional, autoFocusCallback, restoreFocus, restoreFocusCallback, anchorEl, closeButtonProps, maskClosable = true, trigger = 'click', arrow = true, handlerRef, mode, disabled, enterDelay = trigger === 'hover' ? 100 : 0, leaveDelay = trigger === 'hover' ? 100 : 0, backdrop = false, backdropProps, disableBlurClose = false, ...rest } = props;
    const [ariaProps, restProps] = extractAriaProps(rest);
    const [openState, setOpenState] = useMixedValue(open, defaultOpen);
    const [arrowRef, setArrowRef] = useState(null);
    const [rootRef, setRootRef] = useState(null);
    const [anchorNode, setAnchorNode] = useState(anchorEl);
    const { className: okClassName = '', ...restOkProps } = okButtonProps ?? {};
    const childNodeRef = useRef(null);
    const anchorNodeRef = useRef(null);
    useEffect(() => {
        if (anchorEl) {
            setAnchorNode(anchorEl);
            anchorNodeRef.current = anchorEl;
        }
        else if (childNodeRef.current) {
            setAnchorNode(childNodeRef.current);
            anchorNodeRef.current = childNodeRef.current;
        }
    }, [anchorEl]);
    const id = useUniqueId('popover');
    const ariaDescribedbyId = id('content');
    const isInvalid = !!disabled || (!title && !content && !onOk && !onCancel);
    const handleOpen = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        if (openState || isInvalid) {
            return;
        }
        setOpenState(true);
        onOpen?.();
    }, [openState, isInvalid, setOpenState, onOpen]);
    const handleClose = useCallback((e, se) => {
        if (!openState) {
            return;
        }
        setOpenState(false);
        onClose?.(e, se);
    }, [openState, setOpenState, onClose]);
    useLayoutEffect(() => {
        if (isInvalid) {
            handleClose(undefined, { type: 'invalid' });
        }
    }, [isInvalid, handleClose]);
    useImperativeHandle(handlerRef, () => ({
        open: handleOpen,
        close: handleClose,
    }), [handleOpen, handleClose]);
    const handleOpenRef = useRef(handleOpen);
    const handleCloseRef = useRef(handleClose);
    handleOpenRef.current = handleOpen;
    handleCloseRef.current = handleClose;
    const delayedActionTimerRef = useRef();
    const delayedOpen = useCallback(() => {
        if (delayedActionTimerRef.current) {
            clearTimeout(delayedActionTimerRef.current);
        }
        if (enterDelay) {
            delayedActionTimerRef.current = setTimeout(() => {
                handleOpenRef.current();
            }, enterDelay);
        }
        else {
            handleOpen();
        }
    }, [enterDelay, handleOpen]);
    const delayedClose = useCallback((e, se) => {
        if (delayedActionTimerRef.current) {
            clearTimeout(delayedActionTimerRef.current);
        }
        if (leaveDelay) {
            delayedActionTimerRef.current = setTimeout(() => {
                handleCloseRef.current(e, se);
            }, leaveDelay);
        }
        else {
            handleClose(e, se);
        }
    }, [handleClose, leaveDelay]);
    const renderCloseIcon = () => closable ? (React.createElement(Button, { className: popoverClasses.closeIcon, variant: "icon", type: "tertiary", onClick: (e) => {
            handleClose(e, { type: 'closeButtonClick' });
        }, "aria-label": t('close'), ...closeButtonProps },
        React.createElement(IconClose, { size: 20, "aria-hidden": true }))) : null;
    const renderFooter = () => {
        if (footer === null) {
            return null;
        }
        if (!footer || !React.isValidElement(footer)) {
            return !!onCancel || !!onOk ? (React.createElement(Box, { className: popoverClasses.footer },
                React.createElement(FooterButton, { key: "cancel", type: "secondary", onClick: onCancel, onClose: (e) => {
                        handleClose(e, { type: 'cancelButtonClick' });
                    }, ...cancelButtonProps }, cancelText),
                React.createElement(FooterButton, { key: "ok", onClick: onOk, onClose: (e) => {
                        handleClose(e, { type: 'okButtonClick' });
                    }, className: clsx(popoverClasses.okButton, okClassName), ...restOkProps }, okText))) : null;
        }
        const customFooter = React.Children.only(footer);
        return React.cloneElement(customFooter, { ...customFooter.props });
    };
    const shouldEscapeOnClickAway = useCallback((e) => maskClosable && !anchorNodeRef.current?.contains(e.target), [maskClosable]);
    function handleMouseEnter() {
        if (trigger !== 'hover') {
            return;
        }
        delayedOpen();
    }
    function handleMouseLeave() {
        if (trigger !== 'hover') {
            return;
        }
        delayedClose(undefined, { type: 'hoverAway' });
    }
    useEffect(() => {
        if (disableBlurClose) {
            return undefined;
        }
        if (!openState || !maskClosable) {
            return undefined;
        }
        const handleGlobalBlurClose = (e) => {
            delayedClose(e, { type: 'blurAway' });
        };
        // Close the popover when it focuses outside of both root container and child node trigger
        function handleFocus(e) {
            // istanbul ignore else
            if (rootRef) {
                const target = e.target;
                if (!target ||
                    (!rootRef.contains(target) &&
                        !anchorNodeRef.current?.contains(target))) {
                    handleGlobalBlurClose(e);
                }
            }
        }
        document.body.addEventListener('focusin', handleFocus);
        window.addEventListener('blur', handleGlobalBlurClose);
        return () => {
            document.body.removeEventListener('focusin', handleFocus);
            window.removeEventListener('blur', handleGlobalBlurClose);
        };
    }, [openState, maskClosable, delayedClose, rootRef, disableBlurClose]);
    const forkedRef = useForkRef(childNodeRef, children?.ref);
    return (React.createElement(React.Fragment, null,
        children
            ? React.cloneElement(children, {
                ref: forkedRef,
                'aria-describedby': children.props['aria-describedby'] ??
                    (openState ? ariaDescribedbyId : undefined),
                'aria-expanded': children.props['aria-expanded'] ??
                    ((trigger === 'click' || trigger === 'contextMenu') &&
                        open === undefined
                        ? !!openState
                        : undefined),
                onClick: (e) => {
                    // Only click trigger can make the visibility toggleable.
                    // Or the hover - click combination close the popover immediately.
                    if (trigger === 'click' && openState) {
                        delayedClose(e, { type: 'click' });
                    }
                    else if (trigger !== 'contextMenu') {
                        delayedOpen();
                    }
                    return children.props.onClick?.(e);
                },
                onContextMenu: (e) => {
                    // istanbul ignore else
                    if (trigger === 'contextMenu') {
                        e.preventDefault();
                        if (openState) {
                            delayedClose(e, { type: 'contextMenu' });
                        }
                        else {
                            delayedOpen();
                        }
                    }
                    children.props.onContextMenu?.(e);
                },
                onKeyDown: (e) => {
                    if (e.key === 'Escape') {
                        delayedClose(e, { type: 'escapeKeyDown' });
                    }
                    else if (e.shiftKey &&
                        e.key === 'F10' &&
                        trigger === 'contextMenu') {
                        if (openState) {
                            delayedClose(e, { type: 'contextMenuKeyDown' });
                        }
                        else {
                            delayedOpen();
                        }
                    }
                    children.props.onKeyDown?.(e);
                },
                onFocus: (e) => {
                    if (trigger === 'hover' || trigger === 'focus') {
                        // delayedOpen();
                        // We do not delay on focus, since the delay may break VO's announcement for the aria-describedby.
                        if (delayedActionTimerRef.current) {
                            clearTimeout(delayedActionTimerRef.current);
                        }
                        handleOpen();
                    }
                    children.props.onFocus?.(e);
                },
                onMouseEnter: (e) => {
                    handleMouseEnter();
                    children.props.onMouseEnter?.(e);
                },
                onMouseLeave: (e) => {
                    handleMouseLeave();
                    children.props.onMouseLeave?.(e);
                },
            })
            : null,
        anchorNode && backdrop && openState && trigger !== 'hover' && (React.createElement(Backdrop, { open: openState, onClick: (e) => {
                handleClose(e, { type: 'backdropClick' });
            }, ...backdropProps, sx: [
                ...[(theme) => getBackdropStyle(theme)],
                ...(Array.isArray(backdropProps?.sx)
                    ? backdropProps.sx
                    : [backdropProps?.sx]),
            ] })),
        anchorNode && (React.createElement(MUIPopper, { transition: true, open: openState ?? false, ref: setRootRef, modifiers: [
                {
                    name: 'preventOverflow',
                    options: { enabled: true, boundariesElement: 'viewport' },
                },
                {
                    name: 'arrow',
                    options: {
                        enabled: arrow,
                        element: arrowRef,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: [0, arrow ? 10 : 8],
                    },
                },
            ], anchorEl: anchorNode, className: clsx(popoverClasses.root, className), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, role: "presentation", ...restProps }, ({ TransitionProps }) => (React.createElement(Fade, { timeout: 200, ...TransitionProps },
            React.createElement(FocusLimitContainer, { focusBehavior: (loopFocus ?? (!!closable || !maskClosable))
                    ? 'loop'
                    : 'dismiss', "aria-modal": !maskClosable, className: popoverClasses.paper, onEscape: (e) => {
                    let type = 'blurAway';
                    if (e instanceof MouseEvent) {
                        const target = e.target;
                        type = 'clickAway';
                        if (target?.classList?.contains?.(backdropClasses.root)) {
                            // type = 'backdropClick';
                            // The backdrop has a click event listener.
                            // Here we need to interrupt the program to avoid triggering the onClose event twice.
                            return;
                        }
                    }
                    else if (e?.type === 'keydown' &&
                        e?.code === 'Escape') {
                        type = 'escapeKeyDown';
                    }
                    handleClose(e, { type });
                }, id: ariaDescribedbyId, shouldEscapeOnClickAway: shouldEscapeOnClickAway, disableSentinel: loopFocus === undefined ? isTransitional : !loopFocus, autoFocus: autoFocus, autoFocusCallback: autoFocusCallback, restoreFocus: restoreFocus, restoreFocusCallback: restoreFocusCallback, role: "dialog", ...ariaProps },
                arrow ? (React.createElement("span", { ref: setArrowRef, className: popoverClasses.arrow })) : null,
                React.createElement(Box, { sx: { position: 'relative' } },
                    title ? (React.createElement(Box, { className: popoverClasses.title }, title)) : null,
                    renderCloseIcon(),
                    content ? (React.createElement(Box, { className: popoverClasses.content }, content)) : null),
                renderFooter())))))));
};
const StyledPopover = styled(PopoverBase)((props) => {
    const { theme, width, maxWidth, title, content, closable, mode } = props;
    return getStyle(theme, { width, maxWidth, title, content, closable, mode });
});
const Popover = React.forwardRef((props, ref) => (React.createElement(StyledPopover, { ...props, ref: ref })));
export default Popover;
